import React from "react";
import { Loader2 } from "lucide-react";

/* Shared neon palette + UI primitives for the analytics views.
   Mirrors DashboardPage.jsx conventions (Azeret/JetBrains mono labels,
   rgba(13,13,13,.75) cards, neon green #c8ff2a accent). */

export const NEON = "#c8ff2a";
export const PINK = "#ff2a8f";
export const CYAN = "#2ad6ff";
export const PALETTE = [NEON, PINK, CYAN, "#ffffff", "#ffae00", "#9b5cff"];

export const DAY_OPTIONS = [7, 30, 90];

/* mm:ss from milliseconds */
export const fmtMs = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

/* seconds with one decimal from milliseconds */
export const fmtSec = (ms) => `${((Number(ms) || 0) / 1000).toFixed(1)}s`;

export const fmtNum = (n) => (Number(n) || 0).toLocaleString();

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

export const Label = ({ children, className = "" }) => (
  <div className={`text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold ${className}`}>
    {children}
  </div>
);

export const Card = ({ title, action, children, className = "" }) => (
  <section
    className={`p-5 md:p-6 rounded-2xl space-y-4 ${className}`}
    style={{
      background: "rgba(13,13,13,0.75)",
      border: "1px solid rgba(255,255,255,0.12)",
      backdropFilter: "blur(8px)",
    }}
  >
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {title && (
          <h3
            className="font-black uppercase text-sm md:text-base"
            style={{ fontFamily: "'Azeret Mono', monospace", letterSpacing: "-0.03em" }}
          >
            {title}
          </h3>
        )}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Kpi = ({ label, value, sub, accent = NEON }) => (
  <div
    className="p-5 rounded-2xl"
    style={{
      background: "rgba(13,13,13,0.75)",
      border: "1px solid rgba(255,255,255,0.12)",
      backdropFilter: "blur(8px)",
    }}
  >
    <Label>{label}</Label>
    <div
      className="mt-2 font-black tabular-nums"
      style={{ fontFamily: "'Azeret Mono', monospace", fontSize: "1.9rem", color: accent, letterSpacing: "-0.03em" }}
    >
      {value}
    </div>
    {sub != null && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
  </div>
);

export const Loading = ({ label = "Loading analytics…" }) => (
  <div className="flex items-center gap-3 text-white/55 text-sm py-10 justify-center">
    <Loader2 size={16} className="animate-spin" /> {label}
  </div>
);

export const Empty = ({ label = "No data yet." }) => (
  <div className="text-center text-white/45 py-10 border border-dashed border-white/12 rounded-2xl text-sm">
    {label}
  </div>
);

/* Shared days selector (7 / 30 / 90). */
export const DaysSelector = ({ days, onChange }) => (
  <div className="inline-flex items-center gap-2">
    <Label>Range</Label>
    <div className="inline-flex rounded-full overflow-hidden border border-white/12">
      {DAY_OPTIONS.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors"
          style={{
            background: days === d ? NEON : "rgba(255,255,255,0.05)",
            color: days === d ? "#060606" : "rgba(255,255,255,0.7)",
          }}
        >
          {d}d
        </button>
      ))}
    </div>
  </div>
);

/* Simple table primitives matching the dark aesthetic. */
export const Table = ({ children }) => (
  <div className="overflow-x-auto -mx-1">
    <table className="w-full text-sm border-collapse">{children}</table>
  </div>
);

export const Th = ({ children, className = "", ...rest }) => (
  <th
    className={`text-left text-[10px] tracking-[0.22em] uppercase text-white/45 font-bold py-2 px-3 whitespace-nowrap ${className}`}
    {...rest}
  >
    {children}
  </th>
);

export const Td = ({ children, className = "", ...rest }) => (
  <td className={`py-2.5 px-3 text-white/80 border-t border-white/8 align-top ${className}`} {...rest}>
    {children}
  </td>
);

/* Tooltip styling for recharts to match the dark theme. */
export const TOOLTIP_STYLE = {
  background: "rgba(13,13,13,0.95)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: "10px",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "11px",
};
export const TOOLTIP_ITEM = { color: "#fff" };
export const TOOLTIP_LABEL = { color: "rgba(255,255,255,0.6)" };

/* A compact mini bar used inside tables (e.g. share-of-total). */
export const MiniBar = ({ value, max, color = NEON }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 rounded-full bg-white/8 flex-1 min-w-[60px] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-[11px] text-white/55 tabular-nums w-9 text-right">{value}</span>
    </div>
  );
};
